import { useEffect, useMemo, useState, MouseEvent, useRef } from "react";
import {
  Box,
  Button,
  Divider,
  useTheme,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Menu,
  Popper,
  Paper,
  MenuList,
  Grow,
} from "@mui/material";
import { HugeiconsIcon } from "@hugeicons/react";
import { ArrowRight01Icon, PinLocation03Icon, StoreLocation01Icon } from "@hugeicons/core-free-icons";
import { Organization } from "@/types";
import { openBranchDrawer, setActiveBranch, setActiveOrganization } from "@/store/uiSlice";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { Branch } from "@/types/branch";
import { useAccessControl } from "@/features/access-control";

interface Props {
  organizations: Record<string, Organization>;
  onNavigate: (orgId: string) => void;
  onCreateOrganization?: () => void;
}

const OrganizationSelector = ({
  organizations,
  onNavigate,
  onCreateOrganization,
}: Props) => {
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const activeBranch = useAppSelector((state) => state.ui.activeBranch);
  const activeOrganization = useAppSelector(
    (state) => state.ui.activeOrganization
  );
  const user = useAppSelector((state) => state.auth.user);
  const { hasAccess } = useAccessControl();

  useEffect(() => {
    const orgAccess = user?.access?.[activeOrganization];
    if (activeOrganization && orgAccess) {
      if (
        hasAccess("view.branch") &&
        orgAccess?.branches?.length === 1 &&
        !activeBranch
      ) {
        dispatch(setActiveBranch(orgAccess?.branches[0]));
      }
    }
  }, [activeOrganization, user?.access, activeBranch, dispatch]);

  const selectedLabel = useMemo(() => {
    if (!activeOrganization) return "Seleccionar organización";

    const org = organizations[activeOrganization];

    if (activeBranch) {
      const branch = org?.branches?.find((b) => b.id === activeBranch);
      return org?.title + " - " + branch?.title;
    }

    return org?.title ?? "Organización";
  }, [activeOrganization, organizations, activeBranch]);

  // ── Main org menu ──
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const isClosingRef = useRef(false);

  // ── Branch submenu ──
  const [branchAnchorEl, setBranchAnchorEl] = useState<null | HTMLElement>(null);
  const [branchMenuOrgId, setBranchMenuOrgId] = useState<string | null>(null);
  const branchMenuOpen = Boolean(branchAnchorEl);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
    isClosingRef.current = false;
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAll = () => {
    isClosingRef.current = true;
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    setAnchorEl(null);
    setBranchMenuOrgId(null);
    setBranchAnchorEl(null);
  };

  const branchMenuOrg = branchMenuOrgId
    ? organizations[branchMenuOrgId]
    : null;

  // ── Selection helpers ──
  const selectOrgAndBranch = (orgId: string, branchId: Branch['id']) => {
    dispatch(setActiveOrganization(orgId));
    dispatch(setActiveBranch(branchId));
    handleCloseAll();
    onNavigate(orgId);
  };

  const handleOrgHover = (
    event: MouseEvent<HTMLLIElement>,
    org: Organization
  ) => {
    if (!anchorEl || isClosingRef.current) return;

    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
    }

    const target = event.currentTarget;
    hoverTimeoutRef.current = setTimeout(() => {
      const orgAccess = user?.access?.[org.id];
      if (orgAccess?.role === "manager" && orgAccess.branches?.length === 1) {
        setBranchAnchorEl(null);
        setBranchMenuOrgId(null);
        return;
      }
      setBranchAnchorEl(target);
      setBranchMenuOrgId(org.id);
    }, 50);
  };

  return (
    <Box>
      <Button
        fullWidth
        size="large"
        id="org-selector-button"
        aria-controls={open ? "org-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClick}
        sx={{
          justifyContent: "left",
        }}
      >
        {selectedLabel}
      </Button>

      {/* ── Organization list menu ── */}
      <Menu
        id="org-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleCloseAll}
        MenuListProps={{
          "aria-labelledby": "org-selector-button",
        }}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        {Object.values(organizations).map((org) => {
          const isActiveOrg = activeOrganization === org.id;

          return (
            <MenuItem
              key={org.id}
              onMouseEnter={(e) => handleOrgHover(e, org)}
              onClick={() => {
                dispatch(setActiveOrganization(org.id));
                dispatch(setActiveBranch(null));
                handleCloseAll();
                onNavigate(org.id);
              }}
              selected={isActiveOrg}
              sx={{
                "&": {
                  minWidth: 265,
                },
              }}
            >
              <ListItemIcon>
                <HugeiconsIcon
                  icon={PinLocation03Icon}
                  color={palette.primary.main}
                />
              </ListItemIcon>
              <ListItemText>{org.title}</ListItemText>
              <ListItemIcon sx={{ justifyContent: "flex-end" }}>
                {!(
                  user?.access?.[org.id]?.role === "manager" &&
                  user?.access?.[org.id]?.branches?.length === 1
                ) && <HugeiconsIcon icon={ArrowRight01Icon} />}
              </ListItemIcon>
            </MenuItem>
          );
        })}
        {hasAccess("create.organization") && (
          <>

            <Divider />
            <MenuItem>
              <Button
                fullWidth
                size="small"
                color="success"
                onClick={() => {
                  onCreateOrganization?.();
                }}
              >
                Crear Organización
              </Button>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* ── Branch submenu (Popper – no backdrop) ── */}
      <Popper
        open={branchMenuOpen}
        anchorEl={branchAnchorEl}
        placement="right-start"
        transition
        style={{ zIndex: 1300 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper elevation={8}>
              <MenuList autoFocusItem={false}>
                {branchMenuOrg?.branches
                  ?.filter((branch) => {
                    const orgAccess = user?.access?.[branchMenuOrg.id];
                    if (orgAccess?.role === "manager") {
                      return orgAccess.branches?.includes(branch.id);
                    }
                    return true;
                  })
                  .map((branch) => {
                    const isActiveBranch = activeBranch === branch.id;

                    return (
                      <MenuItem
                        key={branch.id}
                        selected={isActiveBranch}
                        onClick={() =>
                          selectOrgAndBranch(branchMenuOrg.id, branch.id)
                        }
                      >
                        <ListItemIcon>
                          <HugeiconsIcon
                            icon={StoreLocation01Icon}
                            color={palette.primary.main}
                            size={20}
                          />
                        </ListItemIcon>
                        <ListItemText>{branch.title}</ListItemText>
                      </MenuItem>
                    );
                  })}
                {hasAccess("create.branch") && branchMenuOrg?.branches?.length ? (
                  <Divider />
                ) : null}
                {/* {user?.access?.[branchMenuOrg?.id ?? ""]?.role !== "manager" && ( */}
                {hasAccess("create.branch") && (
                  <MenuItem>
                    <Button
                      fullWidth
                      size="small"
                      variant="outlined"
                      onClick={() => {
                        if (!branchMenuOrg) return;
                        dispatch(setActiveBranch(null));
                        dispatch(openBranchDrawer());
                        handleCloseAll();
                      }}
                    >
                      Crear Sucursal
                    </Button>
                  </MenuItem>
                )}
              </MenuList>
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default OrganizationSelector;